import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

export type FieldType =
  | "text"
  | "email"
  | "password"
  | "number"
  | "phone"
  | "url"
  | "textarea"
  | "select"
  | "multiselect"
  | "checkbox"
  | "radio"
  | "date"
  | "time"
  | "dateTime"
  | "emoji"
  | "timezone"
  | "dateRange"
  | "color"
  | "state"
  | "zipCode";

export interface DateRangeFieldValue {
  from?: number;
  to?: number;
}

export type FieldValueType<Type extends FieldType> = Type extends "number"
  ? number
  : Type extends "checkbox"
  ? boolean
  : Type extends "multiselect"
  ? string[]
  : Type extends "date" | "time" | "dateTime"
  ? Date | undefined
  : Type extends "dateRange"
  ? DateRangeFieldValue
  : Type extends "emoji" | "timezone" | "color" | "state"
  ? string | undefined
  : string;

/**
 * Validator function for a single form field.
 *
 * @param value - The current value of the field being validated
 * @param allValues - All current form values, useful for cross-field validation
 * @returns Error message string if validation fails, undefined if valid
 *
 * @example
 * const emailValidator: FieldValidator<string> = (value) => {
 *   if (!value.includes('@')) return 'Invalid email address';
 *   return undefined;
 * };
 */
export type FieldValidator<FieldValue = unknown, AllFields extends FieldsConfig = FieldsConfig> = (
  value: FieldValue,
  allValues: FormValues<AllFields>
) => string | undefined;

/**
 * Map of field validators for a form.
 *
 * Each key corresponds to a field name, with an optional validator function.
 *
 * @example
 * const validators: FieldValidators<{ email: string; age: number }> = {
 *   email: (value) => value.includes('@') ? undefined : 'Invalid email',
 *   age: (value) => value >= 18 ? undefined : 'Must be 18 or older'
 * };
 */
export type FieldValidators<AllFields extends FieldsConfig> = {
  [K in keyof AllFields]?: FieldValidator<FieldValueType<AllFields[K]["type"]>, AllFields>;
};

export type FieldSideEffects<T extends FieldsConfig> = {
  [K in keyof T]?: SideEffectsFunction<T[K]["type"], FormValues<T>>;
};

/**
 * Map of field dependencies for re-validation.
 *
 * When a field changes, all fields that list it as a dependency will be re-validated.
 * Useful for scenarios like "confirm password" which depends on "password".
 */
export type FieldDependencies<T extends Record<string, unknown>, D> = {
  [K in keyof T]?: D[];
};

/**
 * Side effects function type that properly infers the field value type
 */
export type SideEffectsFunction<Type extends FieldType, AllValues> = (
  newValue: FieldValueType<Type>,
  allValues: AllValues
) => Partial<AllValues>;

export type FormSideEffects<AllFields extends FieldsConfig> =
  | {
      [K in keyof AllFields]?: SideEffectsFunction<AllFields[K]["type"], FormValues<AllFields>>;
    }
  | ((newValues: FormValues<AllFields>) => Partial<FormValues<AllFields>>);

/**
 * Configuration for a single form field
 *
 * @template Type - The HTML input type for this field
 */
export interface FieldDefinition<Type extends FieldType = FieldType> {
  /** The HTML input type (text, email, number, checkbox, etc.) */
  type: Type;
  /** Initial value for the field - typed according to the field type */
  initialValue: FieldValueType<Type>;
  /** Whether this field is required (sets aria-required attribute) */
  required?: boolean;
  /** Array of field names that should trigger re-validation of this field when they change */
  dependencies?: unknown[];
}

/**
 * Map of field definitions for all form fields
 */
export type FieldsConfig = Record<string, FieldDefinition>;

/**
 * Extract form values type from fields configuration
 */
export type FormValues<Fields extends FieldsConfig> = {
  [K in keyof Fields]: FieldValueType<Fields[K]["type"]>;
};

/**
 * Helper type to get the event type for a field based on its type
 */
type _FieldEventType<Type extends FieldType> = Type extends "checkbox"
  ? React.ChangeEvent<HTMLInputElement>
  : Type extends "select" | "multiselect"
  ? React.ChangeEvent<HTMLSelectElement>
  : Type extends "textarea"
  ? React.ChangeEvent<HTMLTextAreaElement>
  : React.ChangeEvent<HTMLInputElement>;

/**
 * Configuration options for the useForm hook.
 *
 * @template Fields - The fields configuration object
 */
export interface UseFormOptions<Fields extends FieldsConfig> {
  /** Field definitions including type, initialValue, validator, etc. */
  fields: Fields;
  /** Validation logic for each field */
  validators?: FieldValidators<Fields>;
  /** When to run field validation: on blur, on change, or only on submit (default: "submit") */
  validateOn?: "blur" | "change" | "submit";
  /** Callback function invoked on successful form submission (all validations pass) */
  onSubmit?: (values: FormValues<Fields>) => void | Promise<void>;
  /** Function that runs when this field changes, allowing you to update other fields based on the new value. Returns an object with field names as keys and their new values. */
  sideEffects?: FormSideEffects<Fields>;
  /** If the form should be reset on submission or not (defaults to true) */
  resetOnSubmit?: boolean;
}

/**
 * Props object returned by getFieldProps() for binding to input elements.
 *
 * Spread these props onto an input to automatically handle value, change, blur, and ARIA attributes.
 *
 * @template Type - The HTML input type that determines the value type
 *
 * @example
 * const fieldProps = getFieldProps('username');
 * <input {...fieldProps} type="text" />
 */
export interface FieldProps<Type extends FieldType = FieldType> {
  /** Current value of the field - typed according to the field type */
  value: FieldValueType<Type>;
  /** Change handler that updates the field value */
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  /** Blur handler that marks the field as touched and optionally validates */
  onBlur: () => void;
  /** Indicates if the field has a validation error (for screen readers) */
  "aria-invalid"?: boolean;
  /** Associates the field with its error message element (for screen readers) */
  "aria-describedby"?: string;
  /** Indicates if the field is required (for screen readers) */
  "aria-required"?: boolean;
}

// State types
interface FormState<T extends Record<string, unknown>> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
}

type FormAction<T extends Record<string, unknown>> =
  | { type: "SET_FIELD_VALUE"; field: keyof T; value: T[keyof T] }
  | { type: "SET_FIELD_ERROR"; field: keyof T; error: string | undefined }
  | { type: "SET_FIELD_TOUCHED"; field: keyof T }
  | { type: "SET_ERRORS"; errors: Partial<Record<keyof T, string>> }
  | { type: "SET_SUBMITTING"; isSubmitting: boolean }
  | { type: "RESET_FORM"; initialValues: T };

/**
 * Deep equality comparison that handles dates, arrays, and primitive values.
 * Used to determine if a field value has changed from its initial value.
 */
function areValuesEqual(value1: unknown, value2: unknown): boolean {
  // Handle null/undefined
  if (value1 === value2) return true;
  if (value1 === null || value2 === null) return false;

  // Handle Date objects
  if (value1 instanceof Date && value2 instanceof Date) {
    return value1.getTime() === value2.getTime();
  }

  // Handle arrays
  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) return false;
    return value1.every((item, index) => areValuesEqual(item, value2[index]));
  }

  // Handle objects (but not Dates or Arrays which are already handled)
  if (typeof value1 === "object" && typeof value2 === "object") {
    const keys1 = Object.keys(value1);
    const keys2 = Object.keys(value2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) =>
      areValuesEqual((value1 as Record<string, unknown>)[key], (value2 as Record<string, unknown>)[key])
    );
  }

  // Primitive comparison
  return value1 === value2;
}

function formReducer<T extends Record<string, unknown>>(state: FormState<T>, action: FormAction<T>): FormState<T> {
  switch (action.type) {
    case "SET_FIELD_VALUE": {
      const newValues = { ...state.values, [action.field]: action.value };
      return {
        ...state,
        values: newValues,
      };
    }
    case "SET_FIELD_ERROR":
      return {
        ...state,
        errors: action.error
          ? { ...state.errors, [action.field]: action.error }
          : (() => {
              const newErrors = { ...state.errors };
              delete newErrors[action.field];
              return newErrors;
            })(),
      };
    case "SET_FIELD_TOUCHED":
      return {
        ...state,
        touched: { ...state.touched, [action.field]: true },
      };
    case "SET_ERRORS":
      return {
        ...state,
        errors: action.errors,
      };
    case "SET_SUBMITTING":
      return {
        ...state,
        isSubmitting: action.isSubmitting,
      };
    case "RESET_FORM":
      return {
        values: action.initialValues,
        errors: {},
        touched: {},
        isSubmitting: false,
      };
    default:
      return state;
  }
}

/**
 * A flexible form management hook with validation, error handling, and accessibility features.
 *
 * This hook manages form state, field-level validation with configurable timing, dirty/touched tracking,
 * and provides ARIA attributes for accessibility. It handles both synchronous and asynchronous submissions.
 *
 * @template Fields - The fields configuration object defining each field's type, initial value, and validation
 *
 * @param options - Configuration object for the form
 * @param options.fields - Field definitions including type, initialValue, validator, required, dependencies
 * @param options.validateOn - When to run validation: "blur", "change", or "submit" (default: "submit")
 * @param options.onSubmit - Callback invoked when form is submitted successfully
 *
 * @returns Object containing form state and utility functions
 *
 * @example
 * // Basic form with validation
 * const form = useForm({
 *   fields: {
 *     username: {
 *       type: 'text',
 *       initialValue: '',
 *       required: true,
 *     },
 *     email: {
 *       type: 'email',
 *       initialValue: '',
 *       required: true,
 *     },
 *     tags: {
 *       type: 'multiselect',
 *       initialValue: [],
 *       required: false,
 *     },
 *     birthDate: {
 *       type: 'date',
 *       initialValue: undefined,
 *       required: true,
 *     },
 *     appointmentTime: {
 *       type: 'dateTime',
 *       initialValue: undefined,
 *       required: false,
 *     },
 *   },
 *   validators: {
 *     username: (value) => value.length < 3 ? 'Too short' : undefined,
 *     email: (value) => !value.includes('@') ? 'Invalid email' : undefined,
 *     tags: (value) => value.length === 0 ? 'Select at least one tag' : undefined,
 *     birthDate: (value) => value && value > new Date() ? 'Cannot be in the future' : undefined,
 *   },
 *   validateOn: 'blur',
 *   onSubmit: async (values) => {
 *     await api.submitForm(values);
 *   },
 * });
 *
 * // Render form
 * <form onSubmit={form.handleSubmit}>
 *   <input {...form.getFieldProps('username')} />
 *   {form.getFieldError('username').message && (
 *     <span id={form.getFieldError('username').id}>
 *       {form.getFieldError('username').message}
 *     </span>
 *   )}
 *   <select {...form.getFieldProps('tags')} multiple>
 *     <option value="react">React</option>
 *     <option value="typescript">TypeScript</option>
 *     <option value="vite">Vite</option>
 *   </select>
 *   <DatePicker
 *     {...form.getCustomFieldProps('birthDate')}
 *     label="Birth Date"
 *   />
 *   <DateTimePicker
 *     {...form.getCustomFieldProps('appointmentTime')}
 *     label="Appointment"
 *   />
 *   <EmojiPicker {...form.getCustomFieldProps('icon')} />
 *   <button type="submit" disabled={form.isSubmitting}>Submit</button>
 * </form>
 *
 * @example
 * // Form with field dependencies (e.g., password confirmation)
 * const form = useForm({
 *   fields: {
 *     password: {
 *       type: 'password',
 *       initialValue: '',
 *       required: true,
 *     },
 *     confirmPassword: {
 *       type: 'password',
 *       initialValue: '',
 *       required: true,
 *       dependencies: ['password'],
 *     },
 *   },
 *   validators: {
 *     password: (value) => value.length < 8 ? 'Too short' : undefined,
 *     confirmPassword: (value, allValues) =>
 *       value !== allValues.password ? 'Passwords must match' : undefined,
 *   },
 *   onSubmit: (values) => console.log(values),
 * });
 *
 * <input {...form.getFieldProps('password')} type="password" />
 * <input {...form.getFieldProps('confirmPassword')} type="password" />
 *
 * @example
 * // Form with side effects (e.g., auto-calculating total price)
 * const form = useForm({
 *   fields: {
 *     quantity: {
 *       type: 'number',
 *       initialValue: 0,
 *       sideEffects: (newQuantity, allValues) => ({
 *         totalPrice: newQuantity * (allValues.unitPrice as number),
 *       }),
 *     },
 *     unitPrice: {
 *       type: 'number',
 *       initialValue: 0,
 *       sideEffects: (newUnitPrice, allValues) => ({
 *         totalPrice: (allValues.quantity as number) * newUnitPrice,
 *       }),
 *     },
 *     totalPrice: {
 *       type: 'number',
 *       initialValue: 0,
 *     },
 *   },
 *   onSubmit: (values) => console.log(values),
 * });
 *
 * <input {...form.getFieldProps('quantity')} type="number" />
 * <input {...form.getFieldProps('unitPrice')} type="number" />
 * <input {...form.getFieldProps('totalPrice')} type="number" readOnly />
 */
export function useForm<Fields extends FieldsConfig>({
  fields,
  validators,
  validateOn = "submit",
  onSubmit,
  sideEffects,
  resetOnSubmit = false,
}: UseFormOptions<Fields>) {
  type Values = FormValues<Fields>;

  // Extract initial values from field definitions
  const initialValues = useMemo(
    () =>
      Object.keys(fields).reduce((acc, key) => {
        const typedKey = key as keyof Values;
        acc[typedKey] = fields[key]?.initialValue as Values[keyof Values];
        return acc;
      }, {} as Values),
    [fields]
  );

  // Detect unstable initialValue expressions (e.g., inline `new Date()`).
  // If JSON.stringify(initialValues) differs across many consecutive renders, the
  // sync-initial-values effect below will re-dispatch every render and loop.
  const previousInitialValuesStringRef = useRef<string | undefined>(undefined);
  const consecutiveInitialValueChangesRef = useRef(0);
  const hasWarnedAboutUnstableInitialValuesRef = useRef(false);
  if (!hasWarnedAboutUnstableInitialValuesRef.current) {
    const currentInitialValuesString = JSON.stringify(initialValues);
    if (
      previousInitialValuesStringRef.current !== undefined &&
      previousInitialValuesStringRef.current !== currentInitialValuesString
    ) {
      consecutiveInitialValueChangesRef.current += 1;
      if (consecutiveInitialValueChangesRef.current >= 10) {
        hasWarnedAboutUnstableInitialValuesRef.current = true;
        console.error(
          "[useForm] `initialValues` changed across 10 consecutive renders. " +
            "This usually means a field has an unstable `initialValue` expression " +
            "(e.g., inline `new Date()`, `[]`, `{}` that should be stable). " +
            "Stabilize it with `useRef`, `useState` lazy init, or `useMemo`. " +
            "Otherwise the sync-initial-values effect will dispatch every render and may exhaust the update limit.",
          { initialValues }
        );
      }
    } else {
      consecutiveInitialValueChangesRef.current = 0;
    }
    previousInitialValuesStringRef.current = currentInitialValuesString;
  }

  const [state, dispatch] = useReducer(formReducer<Values>, {
    values: initialValues,
    errors: {},
    touched: {},
    isSubmitting: false,
  });

  // Derive dirty state by comparing current values with initial values
  const dirty = useMemo(() => {
    const dirtyFields: Partial<Record<keyof Values, boolean>> = {};
    for (const key in fields) {
      const field = key as keyof Values;
      const currentValue = state.values[field];
      const initialValue = fields[key]?.initialValue as Values[keyof Values];

      if (!areValuesEqual(currentValue, initialValue)) {
        dirtyFields[field] = true;
      }
    }
    return dirtyFields;
  }, [state.values, fields]);

  // Update field values when initialValues change (but only for untouched/clean fields)
  useEffect(() => {
    for (const key in fields) {
      const field = key as keyof Values;
      const newInitialValue = fields[key]?.initialValue as Values[keyof Values];

      // Only update if the field hasn't been touched/modified by the user
      if (!state.touched[field]) {
        if (!areValuesEqual(state.values[field], newInitialValue)) {
          dispatch({ type: "SET_FIELD_VALUE", field, value: newInitialValue });
        }
      }
    }
  }, [JSON.stringify(initialValues)]);

  // Validate a single field
  const validateField = useCallback(
    <K extends keyof Values>(field: K, value: Values[K], allValues: Values): string | undefined => {
      const fieldDef = fields[field as string];

      // Check required validation first
      if (fieldDef?.required) {
        // For boolean fields (checkbox), required is always satisfied
        if (typeof value === "boolean") {
          // Boolean fields are always valid when required
        }
        // For arrays (multiselect), check if empty
        else if (Array.isArray(value)) {
          if (value.length === 0) {
            return "This field is required";
          }
        }
        // For Date fields, check if undefined or null
        else if (
          value instanceof Date ||
          fieldDef.type === "date" ||
          fieldDef.type === "time" ||
          fieldDef.type === "dateTime"
        ) {
          if (value === undefined || value === null) {
            return "This field is required";
          }
        }
        // For dateRange fields, check if both from and to are missing
        else if (fieldDef.type === "dateRange") {
          const rangeValue = value as DateRangeFieldValue;
          if (rangeValue.from === undefined && rangeValue.to === undefined) {
            return "This field is required";
          }
        }
        // For other types, check if falsy (but allow 0 for numbers)
        else if (value === undefined || value === null || value === "") {
          return "This field is required";
        }
      }

      // Apply default validators for specific field types
      if (fieldDef?.type === "email" && typeof value === "string" && value !== "") {
        // Basic email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return "Please enter a valid email address";
        }
      }

      if (fieldDef?.type === "phone" && typeof value === "string" && value !== "") {
        // Phone validation: at least 10 digits (allowing spaces, dashes, parentheses, plus sign)
        const digitsOnly = value.replace(/[\s\-().+]/g, "");
        if (!/^\d{10,}$/.test(digitsOnly)) {
          return "Please enter a valid phone number (at least 10 digits)";
        }
      }

      if (fieldDef?.type === "url" && typeof value === "string" && value !== "") {
        const invalidUrlMessage = "Please enter a valid URL (e.g. https://example.com)";
        if (/\s/.test(value)) {
          return invalidUrlMessage;
        }
        const hasProtocol = /^[a-z][a-z0-9+\-.]*:\/\//i.test(value);
        const candidate = hasProtocol ? value : `https://${value}`;
        try {
          const parsed = new URL(candidate);
          if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return invalidUrlMessage;
          }
          const { hostname } = parsed;
          if (!hostname || (!hostname.includes(".") && hostname !== "localhost")) {
            return invalidUrlMessage;
          }
          const labels = hostname.split(".");
          const labelRegex = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
          if (!labels.every((label) => labelRegex.test(label))) {
            return invalidUrlMessage;
          }
        } catch {
          return invalidUrlMessage;
        }
      }

      if (fieldDef?.type === "zipCode" && typeof value === "string" && value !== "") {
        // US ZIP code: 5 digits, optionally followed by -#### (ZIP+4)
        const zipRegex = /^\d{5}(-\d{4})?$/;
        if (!zipRegex.test(value)) {
          return "Please enter a valid ZIP code (e.g. 12345 or 12345-6789)";
        }
      }

      if (fieldDef?.type === "color" && typeof value === "string" && value !== "") {
        // Hex color validation: 3, 6, or 8-digit hex (with optional leading #)
        const hexRegex = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
        if (!hexRegex.test(value)) {
          return "Please enter a valid hex color (e.g. #FF6B6B)";
        }
      }

      // Then run custom validator if provided (can override default validators)
      const validator = validators?.[field];
      if (!validator) return undefined;
      return validator(value, allValues);
    },
    [validators, fields]
  );

  // Validate all fields and return errors
  const validateAllFields = useCallback(
    (values: Values): Partial<Record<keyof Values, string>> => {
      const errors: Partial<Record<keyof Values, string>> = {};

      for (const field in fields) {
        const error = validateField(field as keyof Values, values[field as keyof Values], values);
        if (error) {
          errors[field as keyof Values] = error;
        }
      }

      return errors;
    },
    [fields, validateField]
  );

  // Validate field and its dependents
  const validateFieldWithDependents = useCallback(
    (field: keyof Values, newValue?: Values[keyof Values], allValues?: Values) => {
      // Use provided values or fall back to state values
      const valueToValidate = newValue !== undefined ? newValue : state.values[field];
      const valuesToUse = allValues || state.values;

      const error = validateField(field, valueToValidate as Values[typeof field], valuesToUse);
      dispatch({ type: "SET_FIELD_ERROR", field, error });

      // Re-validate dependent fields
      for (const dependentFieldKey in fields) {
        const dependentField = dependentFieldKey as keyof Values;
        const fieldDef = fields[dependentFieldKey];
        if (fieldDef?.dependencies?.includes(field as string)) {
          const dependentError = validateField(dependentField, valuesToUse[dependentField], valuesToUse);
          dispatch({ type: "SET_FIELD_ERROR", field: dependentField, error: dependentError });
        }
      }
    },
    [state.values, validateField, fields]
  );

  // Set field value
  const setFieldValue = useCallback(
    <K extends keyof Values>(field: K, value: Values[K]) => {
      dispatch({ type: "SET_FIELD_VALUE", field, value });

      // Execute side effects if defined for this field
      const fieldSideEffects = typeof sideEffects === "object" ? sideEffects?.[field as string] : undefined;
      const globalSideEffect = typeof sideEffects === "function" ? sideEffects : undefined;
      if (fieldSideEffects || globalSideEffect) {
        const newValues = { ...state.values, [field]: value };
        const updates = fieldSideEffects?.(value, newValues) ?? globalSideEffect?.(newValues);

        // Apply all side effect updates
        for (const updateField in updates) {
          const updateValue = updates[updateField];
          if (updateValue !== undefined) {
            dispatch({
              type: "SET_FIELD_VALUE",
              field: updateField as keyof Values,
              value: updateValue as Values[keyof Values],
            });
          }
        }
      }

      // Validate on change if configured
      if (validateOn === "change") {
        // Use setTimeout to ensure state is updated before validation
        // Pass the new value directly to avoid stale closure issues
        setTimeout(() => {
          const newValues = { ...state.values, [field]: value };
          validateFieldWithDependents(field, value, newValues);
        }, 0);
      }
    },
    [sideEffects, validateOn, state.values, validateFieldWithDependents]
  );

  // Set field error
  const setFieldError = useCallback((field: keyof Values, error: string | undefined) => {
    dispatch({ type: "SET_FIELD_ERROR", field, error });
  }, []);

  // Handle field blur
  const handleFieldBlur = useCallback(
    (field: keyof Values) => {
      dispatch({ type: "SET_FIELD_TOUCHED", field });

      // Validate on blur if configured
      if (validateOn === "blur") {
        validateFieldWithDependents(field);
      }
    },
    [validateOn, validateFieldWithDependents]
  );

  // Get props for a field (for native HTML elements)
  const getFieldProps = useCallback(
    <FieldName extends keyof Values>(field: FieldName): FieldProps<Fields[FieldName & string]["type"]> => {
      const fieldDef = fields[field as string];
      const hasError = !!state.errors[field];
      const errorId = `${String(field)}-error`;
      const fieldType = fieldDef?.type;

      // Get the appropriate value based on field type
      const value = state.values[field] ?? fieldDef?.initialValue;

      // Create type-aware onChange handler
      const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        let newValue: Values[FieldName];

        if (fieldType === "checkbox") {
          newValue = (e.target as HTMLInputElement).checked as Values[FieldName];
        } else if (fieldType === "number") {
          newValue = Number((e.target as HTMLInputElement).value) as Values[FieldName];
        } else if (fieldType === "multiselect") {
          // Extract selected values from the select element's options
          const selectElement = e.target as HTMLSelectElement;
          const selectedValues = Array.from(selectElement.selectedOptions).map((option) => option.value);
          newValue = selectedValues as Values[FieldName];
        } else {
          newValue = e.target.value as Values[FieldName];
        }

        setFieldValue(field, newValue);
      };

      return {
        value: value as FieldValueType<Fields[FieldName & string]["type"]>,
        onChange,
        onBlur: () => handleFieldBlur(field),
        "aria-invalid": hasError ? true : undefined,
        "aria-describedby": hasError ? errorId : undefined,
        "aria-required": fieldDef?.required ? true : undefined,
      };
    },
    [state.values, state.errors, setFieldValue, handleFieldBlur, fields]
  );

  // Get props for custom components (like MultiSelect, DatePicker, EmojiPicker, etc.)
  // - emoji fields return onEmojiSelect (matching EmojiPickerProps)
  // - date/time fields return onChange
  // - all other fields return onValueChange
  const getCustomFieldProps = useCallback(
    <FieldName extends keyof Values>(field: FieldName) => {
      type ReturnType = Fields[FieldName & string]["type"] extends "date" | "time" | "dateTime" | "dateRange"
        ? {
            value: FieldValueType<Fields[FieldName & string]["type"]>;
            onChange: (newValue: Values[FieldName]) => void;
            onBlur: () => void;
            "aria-invalid"?: boolean;
            "aria-describedby"?: string;
            "aria-required"?: boolean;
          }
        : Fields[FieldName & string]["type"] extends "emoji"
        ? {
            value: FieldValueType<Fields[FieldName & string]["type"]>;
            onEmojiSelect: (emoji: string) => void;
            onBlur: () => void;
            "aria-invalid"?: boolean;
            "aria-describedby"?: string;
            "aria-required"?: boolean;
          }
        : {
            value: FieldValueType<Fields[FieldName & string]["type"]>;
            onValueChange: (newValue: Values[FieldName]) => void;
            onBlur: () => void;
            "aria-invalid"?: boolean;
            "aria-describedby"?: string;
            "aria-required"?: boolean;
          };
      const fieldDef = fields[field as string];
      const hasError = !!state.errors[field];
      const errorId = `${String(field)}-error`;
      const fieldType = fieldDef?.type;

      // Get the appropriate value based on field type
      const value = state.values[field] ?? fieldDef?.initialValue;

      // DateTimePicker, DateRangeField, and their variants use onChange instead of onValueChange
      const isDateTimeField =
        fieldType === "date" || fieldType === "time" || fieldType === "dateTime" || fieldType === "dateRange";
      // EmojiPicker uses onEmojiSelect instead of onValueChange
      const isEmojiField = fieldType === "emoji";

      return {
        value: value as FieldValueType<Fields[FieldName & string]["type"]>,
        ...(isDateTimeField
          ? {
              onChange: (newValue: Values[FieldName]) => {
                setFieldValue(field, newValue);
              },
            }
          : isEmojiField
          ? {
              onEmojiSelect: (emoji: string) => {
                setFieldValue(field, emoji as Values[FieldName]);
              },
            }
          : {
              onValueChange: (newValue: Values[FieldName]) => {
                setFieldValue(field, newValue);
              },
            }),
        onBlur: () => handleFieldBlur(field),
        "aria-invalid": hasError ? true : undefined,
        "aria-describedby": hasError ? errorId : undefined,
        "aria-required": fieldDef?.required ? true : undefined,
      } as ReturnType; // Type assertion needed due to conditional return type
    },
    [state.values, state.errors, setFieldValue, handleFieldBlur, fields]
  );

  // Get error message for a field
  const getFieldError = useCallback(
    (field: keyof Values): { id: string; message: string | undefined } => {
      return {
        id: `${String(field)}-error`,
        message: state.errors[field],
      };
    },
    [state.errors]
  );

  // Reset form
  const resetForm = useCallback(() => {
    // Recompute initial values from current fields to ensure we use the latest values
    const currentInitialValues = Object.keys(fields).reduce((acc, key) => {
      const typedKey = key as keyof Values;
      acc[typedKey] = fields[key]?.initialValue as Values[keyof Values];
      return acc;
    }, {} as Values);
    dispatch({ type: "RESET_FORM", initialValues: currentInitialValues });
  }, [fields]);

  // Submit form
  const submitForm = useCallback(async () => {
    // Validate all fields
    const errors = validateAllFields(state.values);

    if (Object.keys(errors).length > 0) {
      dispatch({ type: "SET_ERRORS", errors });
      return;
    }

    // Clear errors and submit
    dispatch({ type: "SET_ERRORS", errors: {} });
    dispatch({ type: "SET_SUBMITTING", isSubmitting: true });

    try {
      await onSubmit?.(state.values);
    } finally {
      dispatch({ type: "SET_SUBMITTING", isSubmitting: false });
      if (resetOnSubmit) {
        resetForm();
      }
    }
  }, [validateAllFields, state.values, onSubmit, resetOnSubmit, resetForm]);

  // Handle form submit
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      submitForm();
    },
    [submitForm]
  );

  // Check if form is valid by validating all fields
  // This ensures isValid reflects the actual state, not just displayed errors
  const isValid = useMemo(() => {
    const errors = validateAllFields(state.values);
    return Object.keys(errors).length === 0;
  }, [validateAllFields, state.values]);

  // Get props for the form element
  const getFormProps = useCallback(() => {
    return {
      onSubmit: handleSubmit,
      noValidate: true, // Disable browser validation since we handle it ourselves
    };
  }, [handleSubmit]);

  const isDirty = useMemo(() => Object.values(dirty).some(Boolean), [dirty]);
  const isTouched = useMemo(() => Object.values(state.touched).some(Boolean), [state.touched]);

  return {
    /** Current values of all form fields */
    values: state.values,
    /** Current validation errors for fields (empty object if no errors) */
    errors: state.errors,
    /** Fields that have been focused and blurred (user has interacted with them) */
    touched: state.touched,
    /** Whether the form has any touched fields */
    isTouched,
    /** Fields whose values have been modified from initial values */
    dirty,
    /** Whether the form has any dirty fields */
    isDirty,
    /** Whether the form is currently submitting (true during async onSubmit) */
    isSubmitting: state.isSubmitting,
    /** Whether the form has no validation errors */
    isValid,
    /**
     * Get props to spread onto the form element for automatic binding.
     *
     * @returns Props object with onSubmit handler and noValidate attribute
     *
     * @example
     * <form {...getFormProps()}>
     *   <input {...getFieldProps('email')} type="email" />
     *   <button type="submit">Submit</button>
     * </form>
     */
    getFormProps,
    /**
     * Get props to spread onto an input element for automatic binding.
     *
     * @param field - Name of the field
     * @returns Props object with value, onChange, onBlur, and ARIA attributes
     *
     * @example
     * <input {...getFieldProps('email')} type="email" />
     */
    getFieldProps,
    /**
     * Get props to spread onto custom components (MultiSelect, DatePicker, DateTimePicker, EmojiPicker, etc.) for automatic binding.
     *
     * @param field - Name of the field
     * @returns Props object with value and a change handler appropriate for the field type:
     *   - `onChange` for date/time fields
     *   - `onEmojiSelect` for emoji fields
     *   - `onValueChange` for all other custom fields
     *   Also includes onBlur and ARIA attributes.
     *
     * @example
     * <MultiSelect {...getCustomFieldProps('tags')} options={tagOptions} />
     * <DatePicker {...getCustomFieldProps('birthDate')} label="Birth Date" />
     * <DateTimePicker {...getCustomFieldProps('appointment')} label="Appointment" />
     * <EmojiPicker {...getCustomFieldProps('icon')} />
     */
    getCustomFieldProps,
    /**
     * Get error information for a field.
     *
     * @param field - Name of the field
     * @returns Object with error ID (for aria-describedby) and error message
     *
     * @example
     * const error = getFieldError('email');
     * {error.message && <span id={error.id}>{error.message}</span>}
     */
    getFieldError,
    /**
     * Programmatically set a field's value.
     *
     * @param field - Name of the field
     * @param value - New value for the field
     *
     * @example
     * setFieldValue('email', 'user@example.com');
     */
    setFieldValue,
    /**
     * Programmatically set or clear a field's error.
     *
     * @param field - Name of the field
     * @param error - Error message or undefined to clear
     *
     * @example
     * setFieldError('email', 'This email is already taken');
     * setFieldError('email', undefined); // Clear error
     */
    setFieldError,
    /**
     * Reset the form to its initial state (values, errors, touched, dirty all reset).
     *
     * @example
     * <button type="button" onClick={resetForm}>Reset</button>
     */
    resetForm,
    /**
     * Programmatically submit the form (validates all fields first).
     *
     * @returns Promise that resolves when submission completes
     *
     * @example
     * await submitForm();
     */
    submitForm,
    /**
     * Event handler for form submit events (prevents default and calls submitForm).
     *
     * @param e - Form submit event
     *
     * @example
     * <form onSubmit={handleSubmit}>...</form>
     */
    handleSubmit,
  };
}
